import { create } from 'zustand'
import { readStore, writeStore, migrateLegacyKey } from '../lib/localStore'

// frontend-restructuring §1a — cross-screen shell state that used to live in `Home.jsx`
// `useState` + `window` CustomEvents. Same reference pattern as `useMediaStore`: a plain
// zustand store, read with selectors (`useShellStore(s => s.menuCollapsed)`), written by the
// shell chrome and containers — never by a presentational component.
//
// Slices are added here as each `Home.jsx` `useState` is migrated (one per commit). Right now:
//  - menuCollapsed: the `.control-menu1` collapse toggle (was `isMenuCollapsed` + a
//    `colonny_control_menu_collapsed` localStorage key + a `control-menu-action` window event
//    dispatched by `Menu1.jsx`).

const MENU_COLLAPSED_KEY = 'menuCollapsed'

// Buzz auto-clears after 8s of yellow. The timer lives at module scope so the store owns the
// whole lifecycle — the shell chrome just calls `buzz()` / `stopBuzz()`.
const BUZZ_MS = 8000
let buzzTimer = null

// Move the pre-wrapper key into the versioned store once (`readStore` then reads it normally).
migrateLegacyKey('colonny_control_menu_collapsed', MENU_COLLAPSED_KEY, {
  transform: (raw) => raw === 'true',
})

export const useShellStore = create((set) => ({
  menuCollapsed: readStore(MENU_COLLAPSED_KEY, { fallback: false }) === true,

  setMenuCollapsed: (value) =>
    set(() => {
      writeStore(MENU_COLLAPSED_KEY, !!value)
      return { menuCollapsed: !!value }
    }),

  toggleMenuCollapsed: () =>
    set((state) => {
      const next = !state.menuCollapsed
      writeStore(MENU_COLLAPSED_KEY, next)
      return { menuCollapsed: next }
    }),

  // --- map / media overlay chrome (was `Home.jsx` useState, §1a) ---

  // Active map drawing tool: a hex color, 'eraser', 'wall', 'room', or null.
  drawingColor: null,
  setDrawingColor: (value) => set({ drawingColor: value ?? null }),

  // Map edit lock — gates delete-key + pan inside `Map`.
  isMapLocked: false,
  setIsMapLocked: (value) => set({ isMapLocked: !!value }),

  // The screen-share MediaStream shown fullscreen over Menu1, or null.
  fullScreenStream: null,
  setFullScreenStream: (value) => set({ fullScreenStream: value ?? null }),
  toggleFullScreenStream: (stream) =>
    set((state) => ({ fullScreenStream: state.fullScreenStream ? null : stream })),

  // "Someone buzzed you" — yellow mic pulse, auto-clears after BUZZ_MS.
  isBuzzing: false,
  buzz: () => {
    if (buzzTimer) clearTimeout(buzzTimer)
    buzzTimer = setTimeout(() => {
      buzzTimer = null
      set({ isBuzzing: false })
    }, BUZZ_MS)
    set({ isBuzzing: true })
  },
  stopBuzz: () => {
    if (buzzTimer) {
      clearTimeout(buzzTimer)
      buzzTimer = null
    }
    set({ isBuzzing: false })
  },

  // --- left-rail / Menu2 panel dispatch (was `AppShell` useState, §1b) ---
  //
  // The Menu1 nav click handlers set these optimistically right before `navigate()` so the
  // panel doesn't flash the previous route for a frame; `AppShell`'s `routeDerivedPanel` memo
  // then re-derives the same values from the URL once navigation lands. `panelDataView` also
  // holds the one panel with no URL of its own: `'ai_agent'` (the AI toggle in MenuMedia).
  panelDataView: 'databases',
  panelShowMenu2: false,
  panelForceEmptyMenu: false,
  panelSelectedDoc: null,

  // `handleDataMenuAction` — a DataMenu item was clicked.
  openDataPanel: (dataView) =>
    set({ panelDataView: dataView, panelShowMenu2: true, panelForceEmptyMenu: true, panelSelectedDoc: null }),
  // `handleOpenDocs` — open the docs list in Menu2 without changing the left rail.
  openDocsPanel: () =>
    set({ panelShowMenu2: true, panelForceEmptyMenu: true, panelSelectedDoc: null }),
  // `onAiToggle` (MenuMedia).
  openAiPanel: () =>
    set({ panelDataView: 'ai_agent', panelShowMenu2: true, panelForceEmptyMenu: true, panelSelectedDoc: null }),

  setPanelShowMenu2: (value) => set({ panelShowMenu2: !!value }),
  setPanelForceEmptyMenu: (value) => set({ panelForceEmptyMenu: !!value }),
  setPanelSelectedDoc: (docOrFn) =>
    set((state) => ({
      panelSelectedDoc: typeof docOrFn === 'function' ? docOrFn(state.panelSelectedDoc) : docOrFn,
    })),

  // `.control-menu1` width from the user's last resize drag (px), or null = the route default.
  // Cleared on navigation. Read by both `AppShell` (CSS var) and `SpaceScreen` (the element).
  customMenuWidth: null,
  setCustomMenuWidth: (value) => set({ customMenuWidth: value }),

  // --- file viewer (was the `open-file-media` window CustomEvent, 02.4-databases.md §2 / a″) ---
  //
  // A `celda_filesandmedia` cell used to `window.dispatchEvent` an `open-file-media` event that
  // `Menu2.jsx` caught into local `useState`. The producer now calls `openFileViewer(file)`
  // (threaded as `onOpenFile` from the `DynamicTable` container) and `Menu2.jsx` reads
  // `fileViewerFile` off this store (04-enforce.md 4.3) — the CustomEvent + its listener are
  // gone.
  fileViewerFile: null,
  openFileViewer: (file) => set({ fileViewerFile: file ?? null }),
  closeFileViewer: () => set({ fileViewerFile: null }),

  // --- doc-save flash (was the `doc-saved-success` window CustomEvent) ---
  //
  // `DocContent` calls `onSaved` (threaded from `DocsScreen`, which calls `flashDocSaved`) after
  // a successful doc update; `Menu2.jsx` watches `docSaveFlashAt` and runs its 2s green
  // "Guardado" flash. `Date.now()` (not a bool) so repeated saves each retrigger the effect.
  docSaveFlashAt: 0,
  flashDocSaved: () => set({ docSaveFlashAt: Date.now() }),
}))
